// The order details (KOVA-797) — a port of the mobile app's `features/orders/model/build-order-detail-model.ts`.
// One order's details → what the details view draws: the badge, the "Delivery info" timeline, the shipping and
// payment cards and Track. Copy is the mobile app's `orders.detail.*`, verbatim. Not ported: mobile's "Contact
// your clinic" on a terminal order, which KOVA-798 ships together with its destination (the clinical chat).
import type { Carrier, OrderDetail, OrderTimelineEntry } from '@/api/medOrders'
import { formatArrivalRange, formatCardMask, formatEventTime, formatPhoneNumber, openableUrl } from '@/orders/format'
import { type DisplayStatus, type StatusBadge, detailBadge, displayOrderStatus } from '@/orders/status'

/** The details' fixed labels, for the view. */
export const DETAIL_COPY = {
	title: 'Order details',
	medication: 'Medication',
	deliveryInfo: 'Delivery info',
	shippingDetails: 'Shipping details',
	addressCount: '1 address',
	address: 'Address',
	recipient: 'Recipient',
	paymentDetails: 'Payment details',
	card: 'Card',
} as const

type NodeKey = 'received' | 'reviewing' | 'approved' | 'dispensed' | 'onTheWay' | 'delivered'

export type TimelineKey = NodeKey | 'canceled' | 'deliveryFailed' | 'returned'

/** `failed` and `returned` are the terminal rows; `upcoming` rows are drawn muted. */
export type TimelineState = 'done' | 'upcoming' | 'failed' | 'returned'

/**
 * A presentation hint — the view maps it to an icon. Small rows (the substatus dots) have none. `docApproved` is the
 * stamped page: "Prescription approved" once reached (Figma 14254:162354); still ahead it is the plain `doc`.
 */
export type TimelineGlyph = 'doc' | 'docApproved' | 'truck' | 'home' | 'check' | 'failed' | 'returned'

export interface TimelineItem {
	key: TimelineKey
	title: string
	/** "Jun 23, 10:29 AM" — only on a row an event backs, never on a derived or upcoming one. */
	timestamp: string | null
	state: TimelineState
	glyph: TimelineGlyph | null
	small: boolean
	/** Only on the terminal row. */
	description?: string
}

/**
 * Track: the carrier page, an `https` link (`openableUrl`). Its label depends on where the details are drawn, as
 * Figma has it: the drawer's header (≥ 1024px, 14254:161609) reads "Track order"; the page's sticky button
 * (14254:163882) names the carrier — "Track with UPS", as the mobile app's does — or reads "Track order" too for a
 * carrier this app doesn't know.
 */
export interface DetailTrack {
	label: { drawer: string; page: string }
	url: string
}

export interface OrderDetailModel {
	/** `null` for a status this app doesn't know. */
	badge: StatusBadge | null
	orderNumber: string
	medicationName: string
	/** "Est. delivery 15-18 Jul", or `null` when the backend sent no arrival window (it sends one while shipped). */
	estDelivery: string | null
	timeline: TimelineItem[]
	/** A terminal order: its timeline greys out. */
	timelineMuted: boolean
	/** Country is never shown. */
	shipping: { addressLines: string[]; recipientLines: string[] } | null
	/** Only for a method with a card number (`last4`): PayPal or Klarna has none, so no payment card. */
	payment: { mask: string; brand: string | null } | null
	/** The one action, or `null` for none. */
	track: DetailTrack | null
}

const NODES: Record<NodeKey, { title: string; small: boolean; glyph: 'doc' | 'truck' | 'home' | null }> = {
	received: { title: 'Order received', small: false, glyph: 'doc' },
	reviewing: { title: 'Prescriber reviewing', small: true, glyph: null },
	approved: { title: 'Prescription approved', small: false, glyph: 'doc' },
	dispensed: { title: 'Being dispensed', small: true, glyph: null },
	onTheWay: { title: 'Order is on its way', small: false, glyph: 'truck' },
	delivered: { title: 'Order delivered', small: false, glyph: 'home' },
}

const NODE_ORDER: NodeKey[] = ['received', 'reviewing', 'approved', 'dispensed', 'onTheWay', 'delivered']

/**
 * A row's glyph once it is the last one reached, where it differs from the one it has ahead: Figma swaps the
 * approval's page for the stamped one (14254:162354); still ahead (14226:100203) it is the plain page, like
 * "Order received".
 */
const REACHED_GLYPH: Partial<Record<NodeKey, TimelineGlyph>> = { approved: 'docApproved' }

/** The four rows an order passes through; `reviewing` and `dispensed` are substatus rows in between. */
const MILESTONES: NodeKey[] = ['received', 'approved', 'onTheWay', 'delivered']

/** What a non-terminal order still has ahead of it — never "received", never a substatus row. */
const UPCOMING: NodeKey[] = ['approved', 'onTheWay', 'delivered']

const STATUS_NODE: Partial<Record<DisplayStatus, NodeKey>> = {
	submitted: 'received',
	reviewing: 'reviewing',
	approved: 'approved',
	dispensed: 'dispensed',
	onTheWay: 'onTheWay',
	delivered: 'delivered',
}

// "Order canceled" with one l, as the pill spells it; Figma draws "Order cancelled" (docs/architecture/orders.md, Design deviations).
const TERMINAL: Partial<Record<DisplayStatus, TimelineItem>> = {
	canceled: {
		key: 'canceled',
		title: 'Order canceled',
		timestamp: null,
		state: 'failed',
		glyph: 'failed',
		small: false,
		description: "This order won't continue — progress is no longer shown. Message your clinic with any questions.",
	},
	deliveryFailed: {
		key: 'deliveryFailed',
		title: 'Delivery failed',
		timestamp: null,
		state: 'failed',
		glyph: 'failed',
		small: false,
		description: "The carrier couldn't deliver your order. Please message clinic for more details.",
	},
	returned: {
		key: 'returned',
		title: 'Returned to pharmacy',
		timestamp: null,
		state: 'returned',
		glyph: 'returned',
		small: false,
		description: 'This order was returned to pharmacy — progress is no longer shown.',
	},
}

const TRACK_ORDER = 'Track order'

/** Trademark names, not translated. `unknown` gets the generic "Track order". */
const CARRIER_LABEL: Record<Carrier, string | null> = {
	ups: 'UPS',
	usps: 'USPS',
	fedex: 'FedEx',
	dhl: 'DHL',
	unknown: null,
}

/** The row an event lands on; a terminal event or a mismatched substatus lands nowhere. */
function eventNode(event: OrderTimelineEntry): NodeKey | null {
	switch (event.status) {
		case 'submitted':
			if (event.substatus === 'reviewing') return 'reviewing'
			return event.substatus === 'submitted' || event.substatus === null ? 'received' : null
		case 'approved':
			if (event.substatus === 'dispensed') return 'dispensed'
			return event.substatus === 'approved' || event.substatus === null ? 'approved' : null
		case 'shipped':
			return 'onTheWay'
		case 'delivered':
			return 'delivered'
		default:
			return null
	}
}

/**
 * The rows the order has reached: every row an event backs (the feed's last event per row wins), the
 * current status's own row — a substatus one too, so a "Dispensed" badge never sits over a timeline without
 * it — and every milestone up to the furthest of those, which the feed may have skipped (AC-ORD-47; such a
 * row has no time). The last row reached carries its glyph — "delivered", the last there is, always the
 * house; the approval its stamped page — and the earlier ones a check.
 */
function doneItems(events: OrderTimelineEntry[], statusNode: NodeKey | null): TimelineItem[] {
	const byNode = new Map<NodeKey, OrderTimelineEntry>()
	for (const event of events) {
		const node = eventNode(event)
		if (node) byNode.set(node, event)
	}
	const furthest = Math.max(
		...[...byNode.keys()].map((node) => NODE_ORDER.indexOf(node)),
		statusNode ? NODE_ORDER.indexOf(statusNode) : -1,
	)
	const reached = NODE_ORDER.filter(
		(node, index) => byNode.has(node) || node === statusNode || (MILESTONES.includes(node) && index <= furthest),
	)
	const last = reached[reached.length - 1]
	return reached.map((node) => {
		const { title, small, glyph } = NODES[node]
		const event = byNode.get(node)
		return {
			key: node,
			title,
			timestamp: event ? formatEventTime(event.at) : null,
			state: 'done',
			glyph: small ? null : node === last ? (REACHED_GLYPH[node] ?? glyph) : 'check',
			small,
		}
	})
}

/**
 * A terminal order (canceled, delivery failed, returned) greys out: every row it reached turns into a
 * check, nothing upcoming follows, and one terminal row closes it. Its backfill reaches only as far as its
 * events — the terminal status has no row of its own. An order whose status this app doesn't know shows the
 * rows its events reached and nothing ahead: whether more is coming would be a guess.
 */
function timelineFor(status: DisplayStatus | null, events: OrderTimelineEntry[]) {
	const done = doneItems(events, status ? (STATUS_NODE[status] ?? null) : null)
	const terminal = status ? TERMINAL[status] : undefined
	if (terminal) {
		return {
			muted: true,
			items: [...done.map((item): TimelineItem => (item.small ? item : { ...item, glyph: 'check' })), terminal],
		}
	}
	if (status === null) return { muted: false, items: done }
	const upcoming = UPCOMING.filter((node) => !done.some((item) => item.key === node)).map(
		(node): TimelineItem => ({
			key: node,
			title: NODES[node].title,
			timestamp: null,
			state: 'upcoming',
			glyph: NODES[node].glyph,
			small: false,
		}),
	)
	return { muted: false, items: [...done, ...upcoming] }
}

/**
 * Track only while on the way and only with an `https://` link (the web's rule: the mobile details take any
 * non-null URL) — never once delivered, failed, returned or canceled, even with tracking sent.
 */
function trackFor(status: DisplayStatus | null, detail: OrderDetail): DetailTrack | null {
	const url = status === 'onTheWay' ? openableUrl(detail.tracking?.trackingUrl) : null
	if (!detail.tracking || url === null) return null
	const carrier = CARRIER_LABEL[detail.tracking.carrier]
	return { label: { drawer: TRACK_ORDER, page: carrier ? `Track with ${carrier}` : TRACK_ORDER }, url }
}

export function buildOrderDetailModel(detail: OrderDetail): OrderDetailModel {
	const status = displayOrderStatus(detail.status, detail.substatus)
	const timeline = timelineFor(status, detail.timeline)
	const arrival = formatArrivalRange(detail.arrivalFrom, detail.arrivalTo)
	const { shipping, payment } = detail
	return {
		badge: detailBadge(detail),
		orderNumber: detail.orderNumber,
		medicationName: detail.medicationName,
		estDelivery: arrival ? `Est. delivery ${arrival.text}` : null,
		timeline: timeline.items,
		timelineMuted: timeline.muted,
		shipping: shipping
			? {
					addressLines: [
						shipping.addressLine1,
						...(shipping.addressLine2 ? [shipping.addressLine2] : []),
						`${shipping.city}, ${shipping.state} ${shipping.postalCode}`,
					],
					recipientLines: [shipping.recipient, formatPhoneNumber(shipping.phone)],
				}
			: null,
		payment:
			payment?.last4 != null
				? // The API sends the brand uppercase ("VISA"); the view keys its marks on lowercase (KOVA-441).
					{ mask: formatCardMask(payment.last4), brand: payment.brand?.toLowerCase() ?? null }
				: null,
		track: trackFor(status, detail),
	}
}
